// Mirrors impire-auth/wire/escrow.go — the escrow's verbs, bodies and codes
// (02-DESIGN/02-secrets/03-escrow.md § the verbs) — and the share's one line
// of text from impire-sdk-go/escrow, which the console checks before
// anything is sent, so that a share pasted with a wrong character is refused
// here. JSON field names are the Go files'; a shape that differs is a defect here.

import { subjectOf } from "./auth.ts";

export const VERB_ESCROW = "escrow";
const CHILD = "child";

export const EscrowAction = {
  status: "status",
  collect: "collect",
  resplit: "resplit",
  present: "present",
} as const;

/** escrowSubject is the tokenless form an admin writes: `identity.escrow.<action>`. */
export function escrowSubject(action: string): string {
  return subjectOf(VERB_ESCROW, action);
}

/** escrowChildSubject is a parent's admin's form: `identity.escrow.child.<slug>.<action>`. */
export function escrowChildSubject(child: string, action: string): string {
  return subjectOf(VERB_ESCROW, CHILD, child, action);
}

/** The refusals (§ refusals), beside bad-request. */
export const EscrowCode = {
  stale: "escrow-stale",
  collected: "escrow-collected",
  open: "escrow-open",
  mismatch: "escrow-mismatch",
  notFound: "not-found",
} as const;

/** A slot's state. */
export const SlotState = {
  waiting: "waiting",
  collected: "collected",
  platform: "platform",
  offline: "offline",
} as const;

export const Slot = {
  admin: "admin",
  parent: "parent",
  platform: "platform",
} as const;

export interface Principal {
  subject: string;
  principal?: string;
  display?: string;
}

export interface EscrowPresenter {
  principal?: Principal;
  workspace?: string;
  operator?: boolean;
}

export interface EscrowSlotStatus {
  slot: string;
  state: string;
  collected_by?: EscrowPresenter;
  /** RFC 3339. */
  collected?: string;
}

export interface EscrowPresented {
  slot: string;
  by: EscrowPresenter;
  at: string;
}

export interface EscrowCeremonyStatus {
  reason: string;
  opened_by: EscrowPresenter;
  opened: string;
  expires: string;
  presented: EscrowPresented[];
}

export interface EscrowStatus {
  workspace: string;
  set?: string;
  made?: string;
  cause?: string;
  slots: EscrowSlotStatus[];
  ceremony?: EscrowCeremonyStatus;
}

export interface EscrowCollectReply {
  slot: string;
  set: string;
  share: string;
}

export interface EscrowPresentRequest {
  share: string;
  reason?: string;
}

export const PresentOutcome = {
  opened: "opened",
  presented: "presented",
  recovered: "recovered",
} as const;

export interface EscrowPresentReply {
  outcome: string;
  set?: string;
}

/** waitingToCollect reports whether the workspace's own share waits for an admin. */
export function waitingToCollect(st: EscrowStatus): boolean {
  return st.slots.some((s) => s.slot === Slot.admin && s.state === SlotState.waiting);
}

// The share's text: `impire-recovery-share:<slug>:<set>:<base32>:<checksum>`,
// ninety-seven bytes of share, the checksum the first four bytes of the
// SHA-256 of the text before it, as hex.
export const SHARE_PREFIX = "impire-recovery-share";
const SHARE_SIZE = 97;
const B32 = "abcdefghijklmnopqrstuvwxyz234567";

function base32Length(s: string): number | undefined {
  let bits = 0;
  for (const c of s) {
    if (!B32.includes(c)) {
      return undefined;
    }
    bits += 5;
  }
  return Math.floor(bits / 8);
}

const SLUG = /^[a-z][a-z0-9-]{0,62}$/;
const SET = /^[a-z2-7]{8}$/;

export type ShareCheck = { ok: true; slug: string; set: string } | { ok: false; why: "shape" | "checksum" };

/** checkShare reads a pasted share as the auth service will, before it is sent. */
export async function checkShare(text: string): Promise<ShareCheck> {
  const [prefix, slug, set, bytes, sum, ...rest] = text.trim().split(":");
  if (
    prefix !== SHARE_PREFIX ||
    slug === undefined ||
    set === undefined ||
    bytes === undefined ||
    sum === undefined ||
    rest.length > 0 ||
    !SLUG.test(slug) ||
    !SET.test(set) ||
    base32Length(bytes) !== SHARE_SIZE
  ) {
    return { ok: false, why: "shape" };
  }
  const body = new TextEncoder().encode([prefix, slug, set, bytes].join(":"));
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", body));
  const want = Array.from(digest.slice(0, 4), (b) => b.toString(16).padStart(2, "0")).join("");
  if (want !== sum) {
    return { ok: false, why: "checksum" };
  }
  return { ok: true, slug, set };
}
